/**
 * WeChat Sidebar Quick Reply Page
 * 企业微信侧边栏快捷回复页面
 * 
 * 用于在企业微信群聊侧边栏中快速发送文案和素材
 */

import React, { useState, useEffect } from 'react';
import { Input, Spin, Empty, Button, Tag, message, Modal, Drawer } from 'antd';
import { SearchOutlined, SendOutlined, EditOutlined, FileTextOutlined, PictureOutlined, FolderOutlined, CheckOutlined, CloseOutlined, FileExcelOutlined, FileWordOutlined, FilePdfOutlined, FilePptOutlined, FileZipOutlined, FileOutlined, PlayCircleOutlined, SoundOutlined } from '@ant-design/icons';
import { quickReplyApi, copywritingApi, materialApi, resourceFolderApi } from '../../services/resourceService';
import { Copywriting, Material, QuickReplyItem, ResourceFolder } from '../../types/resource';
import './QuickReply.css';

const { Search } = Input;
const { TextArea } = Input;

// 声明企业微信 JS-SDK 类型
declare const wx: any;

const WeChatQuickReply: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<QuickReplyItem[]>([]);
  const [activeScope, setActiveScope] = useState<'enterprise' | 'personal'>('enterprise'); // 企业/个人
  const [activeType, setActiveType] = useState<'copywriting' | 'material'>('copywriting'); // 文案/素材
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [selectedFolderName, setSelectedFolderName] = useState<string>('全部');
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [folderSearchKeyword, setFolderSearchKeyword] = useState('');
  const [wechatReady, setWechatReady] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingContent, setEditingContent] = useState('');
  const [selectedItem, setSelectedItem] = useState<Copywriting | null>(null);

  // 初始化企业微信 JS-SDK
  useEffect(() => {
    // 开发环境直接设置为就绪状态，方便测试
    if (process.env.NODE_ENV === 'development') {
      setWechatReady(true);
      console.log('开发环境：跳过企业微信 SDK 初始化');
      return;
    }
    
    // 生产环境：尝试初始化，但设置超时保护
    const timeout = setTimeout(() => {
      if (!wechatReady) {
        console.warn('企业微信 SDK 初始化超时，允许页面继续使用（发送功能可能不可用）');
        setWechatReady(true);
      }
    }, 5000); // 5秒超时
    
    initWeChatSDK().finally(() => {
      clearTimeout(timeout);
    });
    
    return () => clearTimeout(timeout);
  }, []);

  // 初始化企业微信 SDK
  const initWeChatSDK = async () => {
    try {
      // 检查 wx 对象是否存在
      if (typeof wx === 'undefined') {
        console.error('企业微信 SDK 未加载，请在企业微信中打开');
        message.warning('请在企业微信中打开此页面');
        setWechatReady(true); // 允许浏览，但发送功能不可用
        return;
      }

      // 获取企业微信配置
      const config = await getWeChatConfig();
      const agentConfig = await getAgentConfig();
      
      // 配置企业微信 JS-SDK
      wx.config({
        beta: true,
        debug: false, // 关闭调试模式
        appId: config.corpId,
        timestamp: config.timestamp,
        nonceStr: config.nonceStr,
        signature: config.signature,
        jsApiList: [], // 企业级 config 不需要声明 API，由 agentConfig 声明
      });

      wx.ready(() => {
        console.log('企业微信 SDK 基础配置成功');
        
        // 配置 Agent Config（侧边栏应用必需）
        wx.agentConfig({
          corpid: agentConfig.corpId,
          agentid: agentConfig.agentId,
          timestamp: agentConfig.timestamp,
          nonceStr: agentConfig.nonceStr,
          signature: agentConfig.signature,
          jsApiList: [
            'sendChatMessage',
            'getCurExternalChat',
            'shareToExternalChat',
            'openEnterpriseChat',
          ],
          success: (res: any) => {
            console.log('企业微信 Agent Config 配置成功', res);
            setWechatReady(true);
          },
          fail: (res: any) => {
            console.error('企业微信 Agent Config 配置失败', res);
            message.warning(`发送功能不可用: ${res.errMsg || '权限配置错误'}`);
            setWechatReady(true); // 允许浏览
          },
        });
      });

      wx.error((res: any) => {
        console.error('企业微信 SDK 基础配置失败:', res);
        // 不显示提示，因为可能 ready 也会触发
      });
    } catch (error) {
      console.error('初始化企业微信 SDK 失败:', error);
      message.warning('初始化失败，发送功能可能不可用');
      setWechatReady(true); // 允许浏览
    }
  };

  // 获取企业微信配置
  const getWeChatConfig = async () => {
    try {
      const { post } = await import('../../utils/request');
      const config = await post<{
        corpId: string;
        timestamp: number;
        nonceStr: string;
        signature: string;
      }>('/wechat/jssdk/config', {
        url: window.location.href.split('#')[0]
      });
      return config;
    } catch (error) {
      console.error('获取企业微信配置失败:', error);
      throw error;
    }
  };

  // 获取 Agent 配置
  const getAgentConfig = async () => {
    try {
      const { post } = await import('../../utils/request');
      const config = await post<{
        corpId: string;
        agentId: string;
        timestamp: number;
        nonceStr: string;
        signature: string;
      }>('/wechat/jssdk/agent-config', {
        url: window.location.href.split('#')[0]
      });
      return config;
    } catch (error) {
      console.error('获取 Agent 配置失败:', error);
      throw error;
    }
  };

  // 构建树形结构
  const buildFolderTree = (folders: ResourceFolder[]): ResourceFolder[] => {
    const folderMap = new Map<number, ResourceFolder & { children?: ResourceFolder[] }>();
    const rootFolders: ResourceFolder[] = [];

    // 先创建所有节点的映射
    folders.forEach(folder => {
      folderMap.set(folder.id, { ...folder, children: [] });
    });

    // 构建树形结构
    folders.forEach(folder => {
      const node = folderMap.get(folder.id)!;
      if (folder.parent_id === null) {
        rootFolders.push(node);
      } else {
        const parent = folderMap.get(folder.parent_id);
        if (parent && parent.children) {
          parent.children.push(node);
        }
      }
    });

    return rootFolders;
  };

  // 过滤文件夹（根据搜索关键词）
  const filterFolders = (folders: ResourceFolder[], keyword: string): ResourceFolder[] => {
    if (!keyword) return folders;
    
    return folders.filter(folder => {
      const matchName = folder.name.toLowerCase().includes(keyword.toLowerCase());
      const hasMatchingChildren = folder.children && filterFolders(folder.children, keyword).length > 0;
      return matchName || hasMatchingChildren;
    });
  };

  // 选择文件夹
  const handleSelectFolder = (folder: ResourceFolder | null) => {
    if (folder) {
      setSelectedFolder(folder.id);
      setSelectedFolderName(folder.name);
    } else {
      setSelectedFolder(null);
      setSelectedFolderName('全部');
    }
    setFolderModalVisible(false);
    setFolderSearchKeyword('');
  };

  // 渲染树形文件夹节点
  const renderFolderNode = (folder: ResourceFolder & { children?: ResourceFolder[] }, level: number = 0) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isSelected = selectedFolder === folder.id;

    return (
      <div key={folder.id}>
        <div
          className={`folder-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${level * 20 + 16}px` }}
          onClick={() => handleSelectFolder(folder)}
        >
          <div className="folder-item-content">
            <FolderOutlined className="folder-icon" />
            <span className="folder-name">{folder.name}</span>
          </div>
          {isSelected && (
            <CheckOutlined className="folder-check" />
          )}
        </div>
        {hasChildren && (
          <div className="folder-children">
            {folder.children!.map(child => renderFolderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };
  const loadFolders = async () => {
    try {
      const result = await resourceFolderApi.getList({
        scope: activeScope,
        folder_type: activeType,
      });
      setFolders(result.items || []);
    } catch (error) {
      console.error('加载分组失败:', error);
    }
  };

  // 加载快捷回复列表
  const loadItems = async () => {
    setLoading(true);
    try {
      const params: any = {
        type: activeType,
        keyword: searchKeyword || undefined,
        scope: activeScope,
        sort: 'popular',
        limit: 50,
      };
      
      if (selectedFolder) {
        params.folder_id = selectedFolder;
      }
      
      console.log('=== 加载快捷回复列表 ===');
      console.log('activeType:', activeType);
      console.log('activeScope:', activeScope);
      console.log('selectedFolder:', selectedFolder);
      console.log('请求参数:', params);
      
      const data = await quickReplyApi.getList(params);
      console.log('返回数据:', data);
      console.log('数据条数:', data.length);
      if (data.length > 0) {
        console.log('第一条数据类型:', data[0].type);
        console.log('第一条数据:', data[0]);
      }
      setItems(data);
    } catch (error) {
      message.error('加载失败');
      console.error('Load quick reply error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 监听参数变化，重新加载数据
  useEffect(() => {
    loadFolders();
    // 切换 scope 或 type 时重置选中的分组
    setSelectedFolder(null);
    setSelectedFolderName('全部');
  }, [activeScope, activeType]);

  useEffect(() => {
    loadItems();
  }, [activeScope, activeType, searchKeyword, selectedFolder]);

  // 发送文案到群聊
  const sendCopywritingToChat = async (copywriting: Copywriting, editedContent?: string) => {
    if (!wechatReady) {
      message.error('企业微信未就绪');
      return;
    }

    try {
      const content = editedContent || copywriting.content;

      // 开发环境模拟发送
      if (process.env.NODE_ENV === 'development') {
        console.log('模拟发送文案:', content);
        message.success('发送成功（开发环境模拟）');
        
        // 记录使用次数
        try {
          await copywritingApi.use(copywriting.id, {
            edited_content: editedContent,
          });
          loadItems();
        } catch (error) {
          console.error('记录使用失败:', error);
        }
        return;
      }

      // 调用企业微信 API 发送消息
      wx.invoke('sendChatMessage', {
        msgtype: 'text',
        text: {
          content: content,
        },
      }, async (res: any) => {
        if (res.err_msg === 'sendChatMessage:ok') {
          message.success('发送成功');
          
          // 记录使用次数
          try {
            await copywritingApi.use(copywriting.id, {
              edited_content: editedContent,
            });
            // 重新加载列表以更新使用次数
            loadItems();
          } catch (error) {
            console.error('记录使用失败:', error);
          }
        } else {
          message.error('发送失败: ' + res.err_msg);
        }
      });
    } catch (error) {
      message.error('发送失败');
      console.error('Send copywriting error:', error);
    }
  };

  // 发送素材到群聊
  const sendMaterialToChat = async (material: Material) => {
    if (!wechatReady) {
      message.error('企业微信未就绪');
      return;
    }

    try {
      // 开发环境模拟发送
      if (process.env.NODE_ENV === 'development') {
        console.log('模拟发送素材:', material.name);
        message.success('发送成功（开发环境模拟）');
        
        await materialApi.use(material.id);
        loadItems();
        return;
      }

      // 显示加载提示
      const hide = message.loading('正在上传素材...', 0);

      try {
        // 1. 先上传到企业微信获取 media_id
        const { post } = await import('../../utils/request');
        const uploadResult = await post<{ media_id: string }>('/wechat/js-sdk/upload-media', {
          file_url: material.file_url,
          file_type: material.file_type === 'image' ? 'image' : 'file',
        });

        hide();

        // 2. 使用 media_id 发送消息
        const msgConfig: any = {
          msgtype: material.file_type === 'image' ? 'image' : 'file',
        };

        if (material.file_type === 'image') {
          msgConfig.image = { mediaid: uploadResult.media_id };
        } else {
          msgConfig.file = { mediaid: uploadResult.media_id };
        }

        wx.invoke('sendChatMessage', msgConfig, async (res: any) => {
          if (res.err_msg === 'sendChatMessage:ok') {
            message.success('发送成功');
            await materialApi.use(material.id);
            loadItems();
          } else {
            message.error('发送失败: ' + res.err_msg);
          }
        });
      } catch (error: any) {
        hide();
        console.error('上传素材失败:', error);
        message.error(error.response?.data?.detail || '上传失败');
      }
    } catch (error) {
      message.error('发送失败');
      console.error('Send material error:', error);
    }
  };

  // 打开编辑弹窗
  const handleEdit = (copywriting: Copywriting) => {
    setSelectedItem(copywriting);
    setEditingContent(copywriting.content);
    setEditModalVisible(true);
  };

  // 保存为个人文案
  const handleSaveAsPersonal = async () => {
    if (!editingContent.trim()) {
      message.warning('内容不能为空');
      return;
    }

    if (!selectedItem) {
      return;
    }

    try {
      const { post } = await import('../../utils/request');
      
      // 生成标题：原标题 + 时间戳
      const timestamp = new Date().toLocaleString('zh-CN', { 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const newTitle = `${selectedItem.title} (${timestamp})`;

      await post('/resources/copywriting', {
        title: newTitle,
        content: editingContent,
        scope: 'personal',
        folder_id: null,
        tags: [],
      });

      message.success('已保存为个人文案');
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败，请重试');
    }
  };

  // 发送编辑后的内容
  const handleSendEdited = () => {
    if (!editingContent.trim()) {
      message.warning('内容不能为空');
      return;
    }
    if (selectedItem) {
      sendCopywritingToChat(selectedItem, editingContent);
      setEditModalVisible(false);
    }
  };

  // 渲染文案卡片
  const renderCopywritingCard = (item: QuickReplyItem) => {
    const copywriting = item.data as Copywriting;
    
    return (
      <div key={`copywriting-${copywriting.id}`} className="copywriting-card">
        <div className="card-title">
          <span className="title-text">{copywriting.title}</span>
          {item.my_usage_count !== undefined && item.my_usage_count > 0 && (
            <Tag color="orange" className="title-badge">
              用过 {item.my_usage_count} 次
            </Tag>
          )}
        </div>
        <div className="card-content">{copywriting.content}</div>
        <div className="card-actions">
          <Button
            className="btn-send"
            icon={<SendOutlined />}
            onClick={() => sendCopywritingToChat(copywriting)}
          >
            发送
          </Button>
          <Button
            className="btn-edit"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedItem(copywriting);
              setEditingContent(copywriting.content);
              setEditModalVisible(true);
            }}
          >
            修改发送
          </Button>
        </div>
      </div>
    );
  };

  // 渲染素材卡片
  const renderMaterialCard = (item: QuickReplyItem) => {
    const material = item.data as Material;
    // 优先使用 thumbnail_url，没有就用 file_url
    const imageUrl = material.thumbnail_url || material.file_url;
    
    // 判断是否为图片类型（包括 webp）
    const isImage = material.file_type === 'image' || 
                    material.content_type?.startsWith('image/') ||
                    material.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/);
    
    // 根据文件类型或扩展名获取图标
    const getFileIcon = () => {
      const ext = material.name.toLowerCase().split('.').pop() || '';
      const iconStyle = { fontSize: 48, color: '#8c8c8c' };
      
      // Excel
      if (['xls', 'xlsx', 'csv'].includes(ext)) {
        return <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
      }
      // Word
      if (['doc', 'docx'].includes(ext)) {
        return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
      }
      // PDF
      if (ext === 'pdf') {
        return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
      }
      // PPT
      if (['ppt', 'pptx'].includes(ext)) {
        return <FilePptOutlined style={{ ...iconStyle, color: '#fa8c16' }} />;
      }
      // 压缩文件
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
        return <FileZipOutlined style={{ ...iconStyle, color: '#722ed1' }} />;
      }
      // 文本文件
      if (['txt', 'md', 'json', 'xml'].includes(ext)) {
        return <FileTextOutlined style={iconStyle} />;
      }
      // 视频
      if (material.file_type === 'video' || ['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
        return <PlayCircleOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
      }
      // 音频
      if (material.file_type === 'audio' || ['mp3', 'wav', 'ogg', 'aac'].includes(ext)) {
        return <SoundOutlined style={{ ...iconStyle, color: '#722ed1' }} />;
      }
      // 默认文件图标
      return <FileOutlined style={iconStyle} />;
    };
    
    return (
      <div key={`material-${material.id}`} className="material-card">
        <div className="card-header">
          <PictureOutlined className="material-icon" />
          <span className="material-name">{material.name}</span>
        </div>
        {isImage && imageUrl ? (
          <div className="card-preview">
            <img src={imageUrl} alt={material.name} />
          </div>
        ) : (
          <div className="card-preview card-preview-icon">
            {getFileIcon()}
          </div>
        )}
        <div className="card-actions">
          <Button
            icon={<SendOutlined />}
            onClick={() => sendMaterialToChat(material)}
          >
            发送
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="wechat-quick-reply-page">
      {/* 顶部 Tab 导航 */}
      <div className="page-tabs">
        <div
          className={`tab-item ${activeScope === 'enterprise' ? 'active' : ''}`}
          onClick={() => setActiveScope('enterprise')}
        >
          企业{activeType === 'copywriting' ? '文案' : '素材'}
        </div>
        <div
          className={`tab-item ${activeScope === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveScope('personal')}
        >
          个人{activeType === 'copywriting' ? '文案' : '素材'}
        </div>
      </div>

      {/* 搜索和筛选区域 */}
      <div className="page-header">
        <Search
          placeholder={`搜索${activeType === 'copywriting' ? '文案' : '素材'}...`}
          allowClear
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          prefix={<SearchOutlined />}
        />

        <div 
          className="folder-selector"
          onClick={() => setFolderModalVisible(true)}
        >
          <FolderOutlined />
          <span>{selectedFolderName}</span>
          <span className="arrow">▼</span>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="page-content">
        {loading ? (
          <div className="loading-container">
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <Empty description="暂无数据" />
          </div>
        ) : (
          <>
            {items.map((item) => {
              if (item.type === 'copywriting') {
                return renderCopywritingCard(item);
              } else {
                return renderMaterialCard(item);
              }
            })}
          </>
        )}
      </div>

      {/* 底部导航 */}
      <div className="page-footer">
        <div
          className={`footer-item ${activeType === 'copywriting' ? 'active' : ''}`}
          onClick={() => setActiveType('copywriting')}
        >
          <FileTextOutlined className="footer-icon" />
          <span>文案</span>
        </div>
        <div
          className={`footer-item ${activeType === 'material' ? 'active' : ''}`}
          onClick={() => setActiveType('material')}
        >
          <PictureOutlined className="footer-icon" />
          <span>素材库</span>
        </div>
      </div>

      {/* 分组选择弹窗 */}
      <Drawer
        title="选择分组"
        placement="bottom"
        height="70vh"
        open={folderModalVisible}
        onClose={() => {
          setFolderModalVisible(false);
          setFolderSearchKeyword('');
        }}
        closeIcon={<CloseOutlined />}
        className="folder-drawer"
      >
        <div className="folder-drawer-content">
          {/* 搜索框 */}
          <Search
            placeholder="搜索分组"
            allowClear
            value={folderSearchKeyword}
            onChange={(e) => setFolderSearchKeyword(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          {/* 全部选项 */}
          <div
            className={`folder-item ${selectedFolder === null ? 'selected' : ''}`}
            onClick={() => handleSelectFolder(null)}
          >
            <div className="folder-item-content">
              <FolderOutlined className="folder-icon" />
              <span className="folder-name">全部</span>
            </div>
            {selectedFolder === null && (
              <CheckOutlined className="folder-check" />
            )}
          </div>

          {/* 树形文件夹列表 */}
          <div className="folder-tree">
            {buildFolderTree(filterFolders(folders, folderSearchKeyword)).map(folder => 
              renderFolderNode(folder)
            )}
          </div>
        </div>
      </Drawer>

      {/* 编辑弹窗 - 改为底部 Drawer */}
      <Drawer
        title="修改发送"
        placement="bottom"
        height="80vh"
        open={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        closeIcon={<CloseOutlined />}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
            <Button
              icon={<EditOutlined />}
              onClick={handleSaveAsPersonal}
            >
              保存为个人文案
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendEdited}
            >
              发送
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 16, padding: '12px', background: '#f0f5ff', borderRadius: 4 }}>
          <span style={{ color: '#1890ff' }}>发送后不会影响原企业文案的内容！</span>
        </div>
        <TextArea
          value={editingContent}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingContent(e.target.value)}
          rows={12}
          placeholder="编辑文案内容..."
          showCount
          maxLength={1000}
          style={{ fontSize: 15, lineHeight: 1.6 }}
        />
      </Drawer>

      {/* 企业微信未就绪提示 - 仅在非开发环境显示 */}
      {!wechatReady && process.env.NODE_ENV === 'production' && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(255,255,255,0.9)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Empty description="正在初始化企业微信..." />
        </div>
      )}
    </div>
  );
};

export default WeChatQuickReply;
